<template>
    <div class="flex flex-col h-screen bg-gray-100">

        <div class="md:sticky top-2 z-topbar">
            <frosted-bar>
                <div class="flex items-center justify-between space-x-4">
                    <h1>Automation Timeline</h1>

                    <div class="flex items-center justify-between space-x-4">
                        <button-secondary
                            @click="restart"
                            :disabled="!selectedTagId || saving"
                        >
                            <i class="fa-solid fa-rotate-left mr-1"></i> Restart Sequence
                        </button-secondary>

                        <button-primary
                            @click="saveTimeline"
                            :disabled="!hasChanges || saving"
                            :loading="saving"
                        >
                            Save Changes
                        </button-primary>
                    </div>
                </div>

                <template #navigation>
                    <a
                        :href="route('crm.leads.view', leadId)+'#deliveries'"
                        class="mf-link text-xs"
                    >
                        <i class="fa-solid fa-arrow-left mr-1 text-xxs"></i>
                        <span>{{ lead?.name }}</span>
                    </a>
                </template>
            </frosted-bar>
        </div>


        <div class="max-w-3xl mx-auto w-full mt-8 px-4 md:px-6 lg:px-8">
            <div class="flex items-center space-x-4">
                <div v-if="tags.length > 0">
                    <label class="mr-2 text-sm font-medium text-gray-700">Select Tag:</label>
                    <input-select
                        v-model:content="selectedTagId"
                        :options="tags.map(tag => ({value: tag.id, label: tag.name}))"
                        class="form-select rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                    >
                        <option
                            v-for="tag in tags"
                            :key="tag.id"
                            :value="tag.id"
                        >
                            {{ tag.name }}
                        </option>
                    </input-select>
                </div>
            </div>
        </div>

        <!-- Scrollable Content -->
        <div class="flex-1 overflow-y-auto px-6">
            <div
                v-if="loading"
                class="py-12 text-center"
            >
                <mf-spinner-medium class="w-12 h-6"/>
            </div>

            <card-basic
                v-else-if="selectedGroup"
                class="max-w-3xl mx-auto bg-white rounded-lg shadow p-6"
            >
                <div class="mb-6">
                    <h2 class="text-lg font-semibold mb-2">{{ selectedGroup.tag_name }}</h2>
                    <p class="text-gray-600 text-sm mb-4">
                        Move the slider to skip emails or move the timeline.
                    </p>

                    <div class="mb-8 px-4">
                        <input
                            type="range"
                            :min="0"
                            :max="selectedGroup.items.length"
                            v-model.number="selectedGroup.sliderValue"
                            class="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                        >
                        <div class="flex justify-between text-xs text-gray-500 mt-2">
                            <span>Start</span>
                            <span>End (Skip All)</span>
                        </div>
                    </div>
                </div>

                <div class="space-y-0 relative pb-8">
                    <!-- Vertical line -->
                    <div
                        class="absolute left-6 top-2 bottom-0 w-0.5 bg-gray-200"
                        style="z-index: 0;"
                    ></div>

                    <div
                        v-for="(item, index) in selectedGroup.items"
                        :key="item.automation_id"
                        class="relative flex items-start pl-14 py-4 group hover:bg-gray-50 rounded transition-colors"
                        :class="{'opacity-60': isSkipped(index)}"
                    >
                        <!-- Dot/Icon -->
                        <div
                            class="absolute left-6 w-8 h-8 rounded-full border-4 flex items-center justify-center bg-white z-10 transition-colors"
                            :class="getStatusClass(item, index)"
                            style="transform: translateX(-50%);"
                        >
                            <i
                                v-if="getStatusIcon(item, index)"
                                :class="getStatusIcon(item, index)"
                                class="text-xs"
                            ></i>
                        </div>

                        <div class="flex-1">
                            <div class="flex justify-between items-baseline">
                                <h3 class="font-semibold text-gray-900">{{ item.automation_name }}</h3>
                                <span class="text-xs text-gray-500 font-mono bg-gray-100 px-2 py-1 rounded">
                                    +{{ formatDelay(item.delay_hours) }}
                                </span>
                            </div>

                            <div class="mt-1 text-sm">
                                <div
                                    v-if="item.status === 'sent'"
                                    class="text-green-600 flex items-center"
                                >
                                    <i class="fa-solid fa-check-circle mr-1.5"></i>
                                    Sent on {{ formatDate(item.sent_at) }}
                                </div>
                                <div
                                    v-else-if="isSkipped(index)"
                                    class="text-gray-500 flex items-center"
                                >
                                    <i class="fa-solid fa-forward mr-1.5"></i>
                                    Skipped (Marked as sent)
                                </div>
                                <div
                                    v-else-if="item.status === 'overdue'"
                                    class="text-red-600 flex items-center"
                                >
                                    <i class="fa-solid fa-exclamation-circle mr-1.5"></i>
                                    Overdue (Will send soon)
                                </div>
                                <div
                                    v-else-if="item.status === 'ignored'"
                                    class="text-gray-400 flex items-center"
                                >
                                    <i class="fa-solid fa-ban mr-1.5"></i>
                                    Ignored (Tag assigned before activation)
                                </div>
                                <div
                                    v-else-if="item.status === 'inactive'"
                                    class="text-gray-400 flex items-center"
                                >
                                    <i class="fa-solid fa-pause mr-1.5"></i>
                                    Inactive (Automation not active yet)
                                </div>
                                <div
                                    v-else
                                    class="text-blue-600 flex items-center"
                                >
                                    <i class="fa-regular fa-clock mr-1.5"></i>
                                    Scheduled for {{ formatDate(item.scheduled_at) }}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </card-basic>

            <div
                v-else-if="tags.length === 0"
                class="text-center py-12 text-gray-500"
            >
                No automations found for this lead.
            </div>

            <div
                v-else
                class="text-center py-12 text-gray-500"
            >
                Select a tag to view its timeline.
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import FrostedBar from "../../../components/layout/pageHeadings/frostedBar.vue";

export default {
    components: {FrostedBar},
    props: {
        leadId: {
            type: Number,
            required: true
        },
        lead: Object
    },
    data() {
        return {
            loading: true,
            saving: false,
            timeline: [],
            groupedTimeline: {}, // Map tag_id -> group
            tags: [],
            selectedTagId: null
        }
    },
    computed: {
        selectedGroup() {
            return this.selectedTagId ? this.groupedTimeline[this.selectedTagId] : null;
        },
        hasChanges() {
            if (!this.selectedGroup) return false;
            return this.selectedGroup.sliderValue !== this.selectedGroup.originalSliderValue;
        }
    },
    mounted() {
        this.loadTimeline();
    },
    methods: {
        async loadTimeline() {
            this.loading = true;
            try {
                const response = await axios.get(route('api.crm.lead.automation.timeline', this.leadId));
                this.timeline = response.data.timeline;
                this.processTimeline();
            } catch (error) {
                console.error('Error loading timeline:', error);
                this.$notify({type: 'error', text: 'Failed to load automation timeline'});
            } finally {
                this.loading = false;
            }
        },
        processTimeline() {
            const groups = {};
            const tagsMap = new Map();

            this.timeline.forEach(item => {
                if (!groups[item.tag_id]) {
                    groups[item.tag_id] = {
                        tag_id: item.tag_id,
                        tag_name: item.tag_name,
                        items: [],
                        sliderValue: 0,
                        originalSliderValue: 0
                    };
                    tagsMap.set(item.tag_id, {id: item.tag_id, name: item.tag_name});
                }
                groups[item.tag_id].items.push(item);
            });

            // Calculate initial slider values
            Object.values(groups).forEach(group => {
                let sentCount = 0;
                group.items.forEach(item => {
                    if (item.status === 'sent') sentCount++;
                });
                group.sliderValue = sentCount;
                group.originalSliderValue = sentCount;
            });

            this.groupedTimeline = groups;
            this.tags = Array.from(tagsMap.values());

            if (this.tags.length > 0 && !this.selectedTagId) {
                this.selectedTagId = this.tags[0].id;
            }
        },
        formatDelay(hours) {
            if (hours < 24) return `${hours}h`;
            return `${Math.floor(hours / 24)}d ${hours % 24}h`;
        },
        formatDate(date) {
            if (!date) return '';
            const d = new Date(date);
            return d.getFullYear() + '-' +
                String(d.getMonth() + 1).padStart(2, '0') + '-' +
                String(d.getDate()).padStart(2, '0') + ' ' +
                String(d.getHours()).padStart(2, '0') + ':' +
                String(d.getMinutes()).padStart(2, '0');
        },
        isSkipped(index) {
            if (!this.selectedGroup) return false;
            return index < this.selectedGroup.sliderValue;
        },
        getStatusClass(item, index) {
            if (item.status === 'sent') return 'border-green-500 text-green-500';
            if (this.isSkipped(index)) return 'border-gray-400 text-gray-400 bg-gray-100';
            if (item.status === 'overdue') return 'border-red-500 text-red-500';
            if (item.status === 'ignored' || item.status === 'inactive') return 'border-gray-300 text-gray-300';
            return 'border-blue-500 text-blue-500';
        },
        getStatusIcon(item, index) {
            if (item.status === 'sent') return 'fa-solid fa-check';
            if (this.isSkipped(index)) return 'fa-solid fa-forward';
            if (item.status === 'overdue') return 'fa-solid fa-exclamation';
            if (item.status === 'ignored') return 'fa-solid fa-ban';
            if (item.status === 'inactive') return 'fa-solid fa-pause';
            return 'fa-regular fa-clock';
        },
        async restart() {
            if (!this.selectedTagId) return;
            if (!confirm('Are you sure you want to restart the sequence? This will delete all delivery records for this tag and reset the start time.')) return;

            this.saving = true;
            try {
                await axios.post(route('api.crm.lead.automation.update', this.leadId), {
                    tag_id: this.selectedTagId,
                    action: 'restart'
                });
                this.$notify({type: 'success', text: 'Sequence restarted'});
                await this.loadTimeline();
            } catch (error) {
                console.error('Error restarting sequence:', error);
                this.$notify({type: 'error', text: 'Failed to restart sequence'});
            } finally {
                this.saving = false;
            }
        },
        async saveTimeline() {
            if (!this.selectedGroup) return;

            this.saving = true;
            try {
                const group = this.selectedGroup;
                let targetAutomationId = null;

                if (group.sliderValue < group.items.length) {
                    targetAutomationId = group.items[group.sliderValue].automation_id;
                }

                await axios.post(route('api.crm.lead.automation.update', this.leadId), {
                    tag_id: group.tag_id,
                    action: 'skip_to',
                    target_automation_id: targetAutomationId
                });

                this.$notify({type: 'success', text: 'Timeline updated'});
                await this.loadTimeline();
            } catch (error) {
                console.error('Error updating timeline:', error);
                this.$notify({type: 'error', text: 'Failed to update timeline'});
            } finally {
                this.saving = false;
            }
        }
    }
}
</script>
